import React, { useEffect, useState, useRef } from 'react';
import api from '../utils/api';

export default function AdminSettings() {
  const [settings, setSettings] = useState({ bankName: '', accountName: '', accountNumber: '', eventName: '', maxTickets: 500, bankQRImage: '', logoImage: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [qrFile, setQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const qrRef = useRef();
  const logoRef = useRef();

  useEffect(() => {
    api.get('/admin/settings').then(r => {
      setSettings(r.data);
      if (r.data.bankQRImage) setQrPreview(`/uploads/bankqr/${r.data.bankQRImage}`);
      if (r.data.logoImage) setLogoPreview(`/uploads/logo/${r.data.logoImage}`);
    });
  }, []);

  const handleChange = (e) => setSettings(s => ({ ...s, [e.target.name]: e.target.value }));

  const handleQRFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setQrFile(file);
    setQrPreview(URL.createObjectURL(file));
  };

  const handleLogoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('bankName', settings.bankName);
      fd.append('accountName', settings.accountName);
      fd.append('accountNumber', settings.accountNumber);
      fd.append('eventName', settings.eventName);
      fd.append('maxTickets', settings.maxTickets);
      if (qrFile) fd.append('bankQRImage', qrFile);
      if (logoFile) fd.append('logoImage', logoFile);
      const res = await api.put('/admin/settings', fd);
      setSettings(res.data);
      setMsg('Settings saved successfully');
    } catch {
      setMsg('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Settings</h1>
        <p className="admin-page-sub">Site logo, payment info, and event configuration</p>
      </div>

      <form onSubmit={save} style={{ maxWidth: 640 }}>

        {/* ─── Site Logo ─── */}
        <div className="admin-card">
          <div className="admin-card-title">🌐 Site Logo</div>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', marginBottom: 16 }}>
            Displays in the center of the navigation bar on the public landing page.
          </p>
          <div
            style={{ border: '2px dashed rgba(212,175,55,0.3)', borderRadius: 16, padding: 24, textAlign: 'center', cursor: 'pointer', background: 'rgba(212,175,55,0.04)', transition: 'all 0.2s' }}
            onClick={() => logoRef.current.click()}
          >
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" style={{ width: 100, height: 100, objectFit: 'contain', borderRadius: '50%', border: '2px solid rgba(212,175,55,0.3)' }} />
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 14 }}>🖼️ Click to upload logo image</div>
            )}
            <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>PNG with transparent background recommended</div>
          </div>
          <input ref={logoRef} type="file" style={{ display: 'none' }} accept="image/*" onChange={handleLogoFile} />
        </div>

        {/* ─── Bank Payment ─── */}
        <div className="admin-card">
          <div className="admin-card-title">🏦 Bank Payment Details</div>
          <div className="admin-field">
            <label className="admin-label">Bank Name</label>
            <input className="admin-input" name="bankName" value={settings.bankName} onChange={handleChange} placeholder="e.g. ABA Bank" />
          </div>
          <div className="admin-field">
            <label className="admin-label">Account Name</label>
            <input className="admin-input" name="accountName" value={settings.accountName} onChange={handleChange} placeholder="e.g. ALS Organization" />
          </div>
          <div className="admin-field">
            <label className="admin-label">Account Number</label>
            <input className="admin-input" name="accountNumber" value={settings.accountNumber} onChange={handleChange} placeholder="e.g. 000-123-456" />
          </div>
          <div className="admin-field">
            <label className="admin-label">Bank QR Code Image</label>
            <div
              style={{ border: '2px dashed rgba(212,175,55,0.25)', borderRadius: 12, padding: 24, textAlign: 'center', cursor: 'pointer', background: 'rgba(212,175,55,0.03)' }}
              onClick={() => qrRef.current.click()}
            >
              {qrPreview
                ? <img src={qrPreview} alt="Bank QR" style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8 }} />
                : <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>🏦 Click to upload bank QR image</div>}
            </div>
            <input ref={qrRef} type="file" style={{ display: 'none' }} accept="image/*" onChange={handleQRFile} />
          </div>
        </div>

        {/* ─── Password Info ─── */}
        <div className="admin-card">
          <div className="admin-card-title">🔐 Admin Password</div>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', marginBottom: 12 }}>
            Change the admin password in <code style={{ color: '#d4af37' }}>server/.env</code> (ADMIN_PASSWORD) and restart the server.
          </p>
          <div style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.3)', padding: 12, borderRadius: 8, fontSize: 13, color: '#4ade80' }}>
            ADMIN_PASSWORD=your_new_password
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button className="admin-btn" type="submit" disabled={saving} style={{ padding: '12px 32px' }}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          {msg && <span style={{ fontSize: 14, color: msg.includes('fail') ? '#f87171' : '#4ade80' }}>{msg}</span>}
        </div>
      </form>
    </div>
  );
}
